'use client';

import { type ReactNode, useMemo, useState } from 'react';
import type { VariantType } from '../../../../types/variant';
import {
  RECONSENT_COPY,
  RECONSENT_COPY_FALLBACK,
  RECONSENT_DISPLAY_ORDER,
  RECONSENT_GA_EVENT,
  type ReconsentCopy,
} from '../../constants';
import type { PendingReconsentTerm, TermCategory } from '../../types';
import { clearReconsentDismissedAt, saveReconsentDismissedAt } from '../../utils';
import ReconsentLeadText from '../ReconsentLeadText/ReconsentLeadText';
import RequiredConsentModal from '../RequiredConsentModal/RequiredConsentModal';

export type RequiredConsentFlowProps = {
  /**
   * 表示中のユーザーのID。キャンセル時の抑制・同意時の抑制解除をユーザーごとに記録するために使う。
   * 未ログイン等で特定できない場合は undefined を渡す（抑制の記録・解除を行わない）。
   */
  userId: number | undefined;
  /** `GET /term-consent/pending` の terms。空なら何も描画しない */
  terms: PendingReconsentTerm[];
  /** 同意ログの登録。呼び出し側で `POST /term-consent` を注入する */
  onAgree: (oemTermId: number) => Promise<void>;
  /**
   * 全文書の表示が終わったときに呼ばれる。
   * `isAllAgreed` が true のときだけ、中断していた遷移を続行してよい。
   */
  onFinished?: (isAllAgreed: boolean) => void;
  /**
   * 同意ログの登録に失敗したときの通知。呼び出し側でエラーダイアログを出す。
   * 注入されない場合は何も表示されないため、原則として注入する。
   */
  onError?: (error: unknown) => void;
  /** GA イベント送信。注入されない場合は送信しない */
  pushDataLayer?: (data: Record<string, unknown>) => void;
  /**
   * キャンセル時に訪問時モーダル（表示層①）を24時間抑制するか。
   * 抑制はユーザーごと・規約タイプごとに記録するため、ある規約をキャンセルしても
   * 他に同意が必要な規約のモーダルは引き続き表示される。
   */
  suppressOnCancel?: boolean;
  /** 規約タイプごとの文言の上書き。OEM 固有の文書名に合わせる用途 */
  copyOverrides?: Partial<Record<TermCategory, Partial<ReconsentCopy>>>;
  /**
   * 見出し下のリード文の上書き。
   * 未指定時は `variant` と規約タイプの文言から自動生成する（通常は指定しない）。
   */
  leadText?: ReactNode;
  variant?: VariantType;
};

/**
 * 文書 再承諾の連続表示フロー。
 *
 * 1文書＝1モーダルで、規約 → プライバシーポリシー → その他の順に連続表示する。
 * 1つ目を閉じた（同意・キャンセルいずれでも）後に次を開く。同意した文書のみ
 * 同意ログを登録するため、部分同意（1つ目に同意し2つ目をキャンセル）も成立する。
 * 拒否は登録せず GA イベントのみ送る。
 */
function RequiredConsentFlow({
  userId,
  terms,
  onAgree,
  onFinished,
  onError,
  pushDataLayer,
  suppressOnCancel = true,
  copyOverrides,
  leadText,
  variant = 'default',
}: RequiredConsentFlowProps) {
  // 表示順を固定する。API のレスポンス順には依存しない。
  // 定義にない未知のカテゴリは indexOf が -1 を返して先頭に来てしまうため、末尾に送る
  const orderedTerms = useMemo(() => {
    const orderOf = (category: TermCategory): number => {
      const index = RECONSENT_DISPLAY_ORDER.indexOf(category);
      return index < 0 ? RECONSENT_DISPLAY_ORDER.length : index;
    };
    return [...terms].sort((a, b) => orderOf(a.category) - orderOf(b.category));
  }, [terms]);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [agreedCount, setAgreedCount] = useState(0);

  const currentTerm = orderedTerms[currentIndex];

  const goToNext = (nextAgreedCount: number) => {
    const nextIndex = currentIndex + 1;
    setCurrentIndex(nextIndex);
    if (nextIndex >= orderedTerms.length) {
      onFinished?.(nextAgreedCount === orderedTerms.length);
    }
  };

  const handleAgree = async () => {
    if (!currentTerm || isSubmitting) return;

    setIsSubmitting(true);
    try {
      await onAgree(currentTerm.oemTermId);
      pushDataLayer?.({
        event: RECONSENT_GA_EVENT.agree,
        termCategory: currentTerm.category,
        oemTermId: currentTerm.oemTermId,
      });
      // 同意できたので当該ユーザーの当該規約タイプの抑制を解除する
      clearReconsentDismissedAt(userId, currentTerm.category);
      const nextAgreedCount = agreedCount + 1;
      setAgreedCount(nextAgreedCount);
      goToNext(nextAgreedCount);
    } catch (error) {
      // 登録に失敗した場合はモーダルを閉じない（同意が記録されないまま先に進めない）。
      // 何も通知しないと「押したのに閉じない」だけに見えて再試行の判断ができないため、
      // 呼び出し側にエラーを伝える
      onError?.(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    if (!currentTerm || isSubmitting) return;

    // 拒否は DB に記録しない。GA イベントのみ送る
    pushDataLayer?.({
      event: RECONSENT_GA_EVENT.cancel,
      termCategory: currentTerm.category,
      oemTermId: currentTerm.oemTermId,
    });
    if (suppressOnCancel) saveReconsentDismissedAt(userId, currentTerm.category);
    goToNext(agreedCount);
  };

  if (!currentTerm) return null;

  // API 側にカテゴリが追加された場合でも落ちないようフォールバックする
  const defaultCopy = RECONSENT_COPY[currentTerm.category] ?? RECONSENT_COPY_FALLBACK;
  const override = copyOverrides?.[currentTerm.category];
  // 上書きは項目ごとに解決する。スプレッドでマージすると、`href: undefined` を
  // 明示的に渡された場合にデフォルトのリンク先を消してしまうため
  const copy: ReconsentCopy = {
    title: override?.title ?? defaultCopy.title,
    checkboxLabel: override?.checkboxLabel ?? defaultCopy.checkboxLabel,
    documentName: override?.documentName ?? defaultCopy.documentName,
    href: override?.href ?? defaultCopy.href,
  };

  return (
    <RequiredConsentModal
      // 次の文書に切り替わったときにチェック状態を持ち越さないため再マウントさせる
      key={currentTerm.oemTermId}
      isOpen
      title={copy.title}
      leadText={leadText ?? <ReconsentLeadText copy={copy} variant={variant} />}
      termHtml={currentTerm.term}
      checkboxLabel={copy.checkboxLabel}
      onAgree={handleAgree}
      onCancel={handleCancel}
      isSubmitting={isSubmitting}
      variant={variant}
    />
  );
}

export default RequiredConsentFlow;
